import React, {
  Dispatch,
  Fragment,
  SetStateAction,
  SyntheticEvent,
  useEffect,
  useState,
} from 'react';
import { formatToTz } from 'utils/date-format';
import CURRENCY from 'utils/currency';
import Table, { ColumnConfig } from 'components/table';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import { Badge } from 'components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from 'components/ui/tabs';
import { cn } from 'lib/utils';
import Pagination from 'components/pagination';
import { useLocation, useNavigate } from 'react-router-dom';
import useQuery from 'utils/hooks/useQuery';
import QUERY from 'utils/query';
import useUnpaidTickets from 'state/ticket/hooks/useUnpaidTickets';
import { Theme } from '@table-library/react-table-library/types/theme';
import TableToolbar from './TableToolbar';
import useLastUpdatedTimer from './hooks/useLastUpdatedTimer';

interface IUnpaidTicket {
  orderId: string;
  orderNumber: string;
  customerName: string;
  grossAmount: number;
  orderType: string;
  packedBy: string;
  dateCreated: string;
  status: string;
  action?: string;
}

interface IProps {
  dateFrom: string;
  dateTo: string;
  isActiveTab: boolean;
  onTicketSelect?: (ticketData: IUnpaidTicket) => void;
  setIsUnpaidTicketFlow: Dispatch<SetStateAction<boolean>>;
}

const STATUS_BADGE_MAP: Record<
  string,
  {
    variant: 'default' | 'secondary' | 'destructive' | 'outline';
    label: string;
    className?: string;
  }
> = {
  PACKED_READY: {
    variant: 'default',
    label: 'PACKED & READY',
    className: 'bg-green-400 text-white',
  },
  IN_PROCESS: {
    variant: 'default',
    label: 'IN-PROCESS',
    className: 'bg-yellow-500 text-white',
  },
  READY: {
    variant: 'default',
    label: 'Ready',
    className: 'bg-green-500 text-white',
  },
  OUT_FOR_DELIVERY: { variant: 'secondary', label: 'OUT FOR DELIVERY' },
  AWAITING_PROCESSING: { variant: 'secondary', label: 'AWAITING PROCESSING' },
};

const ticketStatusFormatter = (status: string): JSX.Element => {
  const config = STATUS_BADGE_MAP[status];
  if (!config) {
    return <Badge variant="secondary">{status}</Badge>;
  }
  return (
    <Badge variant={config.variant} className={config.className}>
      {config.label}
    </Badge>
  );
};

const TICKET_STATUS_TABS = [
  { label: 'ALL', value: 'ALL' },
  { label: 'Verification Pending', value: 'VERIFICATION_PENDING' },
  { label: 'Awaiting Processing', value: 'AWAITING_PROCESSING' },
  { label: 'In Process', value: 'IN_PROCESS' },
  { label: 'Packed & Ready', value: 'PACKED_READY' },
  { label: 'Out For Delivery', value: 'OUT_FOR_DELIVERY' },
];

const ITEMS_PER_PAGE = 25;

const STICKY_CELL_CLASS =
  'sticky right-0 bg-white z-10 min-w-30 max-w-30 shadow-[-2px_0_4px_rgba(0,0,0,0.1)] flex items-center justify-center';

const UnpaidTicketsTab = ({
  dateFrom,
  dateTo,
  isActiveTab,
  onTicketSelect,
  setIsUnpaidTicketFlow,
}: IProps): JSX.Element => {
  const { unpaidTickets, isLoading, fetchTickets } = useUnpaidTickets();
  const { organisation } = useOrganisation();
  const { settings } = organisation;
  const [currentPage, setCurrentPage] = useState(1);
  const [triggerRefresh, setTriggerRefresh] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const urlParams = useQuery(location.search);
  const { lastUpdateMessage, resetTimer } = useLastUpdatedTimer(triggerRefresh);

  const activeStatus = urlParams.get('status')?.toUpperCase() || 'ALL';
  const searchString = urlParams.get('search')?.toLowerCase() || '';

  const filteredTickets = unpaidTickets.filter((ticket) => {
    const matchesStatus =
      activeStatus === 'ALL' ? true : ticket.status === activeStatus;
    const matchesSearch = searchString
      ? ticket.orderNumber.toLowerCase().includes(searchString) ||
        ticket.customerName.toLowerCase().includes(searchString) ||
        ticket.packedBy.toLowerCase().includes(searchString)
      : true;
    return matchesStatus && matchesSearch;
  });

  const totalPages = Math.ceil(filteredTickets.length / ITEMS_PER_PAGE);
  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  useEffect(() => {
    if (isActiveTab) {
      fetchTickets();
    }
  }, [isActiveTab, dateFrom, dateTo, triggerRefresh]);

  const handleRefresh = () => {
    setTriggerRefresh((prev) => !prev);
    resetTimer();
  };

  const columns: ColumnConfig<IUnpaidTicket>[] = [
    { key: 'orderNumber', label: 'Ticket ID', sortable: true },
    {
      key: 'customerName',
      label: 'Customer',
      sortable: true,
      renderCell: (row) => <div className="capitalize">{row.customerName}</div>,
    },
    {
      key: 'grossAmount',
      label: 'Gross Amount',
      sortable: true,
      renderCell: (row) => (
        <div className="text-gray-blue-60 font-medium">
          {CURRENCY.convertToMainUnit(row.grossAmount)}
        </div>
      ),
    },
    { key: 'orderType', label: 'Order Type', sortable: true },
    { key: 'packedBy', label: 'Packed By', sortable: true },
    {
      key: 'dateCreated',
      label: 'Date Created',
      sortable: true,
      isDate: true,
      renderCell: (row) => (
        <>
          {formatToTz(row.dateCreated, settings?.timezone, 'MMM dd, hh:mm a')}
        </>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      renderCell: (row) => ticketStatusFormatter(row.status),
    },
    {
      key: 'action',
      label: 'Action',
      classNameCell: STICKY_CELL_CLASS,
      renderCell: (row) => (
        <span
          className="text-dark-green-link cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            if (onTicketSelect) {
              onTicketSelect(row);
              navigate({
                search: QUERY.update(location.search, { tab: 'checkout' }),
              });
            }
            setIsUnpaidTicketFlow(true);
          }}
        >
          Checkout &rarr;
        </span>
      ),
    },
  ];

  const handleRowClick = (row: IUnpaidTicket, _: SyntheticEvent) => {
    if (onTicketSelect) {
      onTicketSelect(row);
      navigate({
        search: QUERY.update(location.search, { tab: 'checkout' }),
      });
      setIsUnpaidTicketFlow(true);
    }
  };

  const handleStatusChange = (status: string) => {
    setCurrentPage(1);
    navigate({
      search: QUERY.update(location.search, { status: status.toLowerCase() }),
    });
  };

  const prevPage = () => currentPage > 1 && setCurrentPage(currentPage - 1);
  const nextPage = () =>
    currentPage < totalPages && setCurrentPage(currentPage + 1);

  const customTheme: Theme = {
    Table: `
      grid-template-columns: minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) minmax(120px, 200px) 120px !important;
    `,
    BaseCell: `
    &:nth-of-type(8) {
      position: sticky !important;
      right: 0px !important;
      background-color: white !important;
      z-index: 10 !important;
      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1) !important;
      min-width: 120px !important;
      max-width: 120px !important;
    }
  `,
    HeaderCell: `
    &:nth-of-type(8) {
      position: sticky !important;
      right: 0px !important;
      background-color: white !important;
      z-index: 11 !important;
      box-shadow: -2px 0 4px rgba(0, 0, 0, 0.1) !important;
      font-weight: 600 !important;
      min-width: 120px !important;
      max-width: 120px !important;
    }
  `,
  };

  return (
    <Fragment>
      <TableToolbar
        queryParams={location.search}
        onRefresh={handleRefresh}
        lastUpdateMessage={lastUpdateMessage}
      />

      <Tabs
        value={activeStatus}
        onValueChange={handleStatusChange}
        className="w-full"
      >
        <TabsList className="w-full justify-start rounded-none border-b bg-transparent p-0 h-auto gap-5 mb-3 overflow-x-auto flex-nowrap">
          {TICKET_STATUS_TABS.map((tab) => (
            <TabsTrigger
              key={tab.value}
              value={tab.value}
              className={cn(
                'rounded-none border-b-2 border-transparent whitespace-nowrap',
                'data-[state=active]:border-treez-green data-[state=active]:text-gray-blue-80 data-[state=active]:bg-transparent data-[state=active]:shadow-none',
                'px-0 text-muted-foreground hover:text-gray-blue-80'
              )}
            >
              {tab.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="overflow-x-auto overflow-y-hidden relative w-full">
        <Table
          data={paginatedTickets}
          columns={columns}
          onRowClick={handleRowClick}
          isLoading={isLoading}
          isEmpty={filteredTickets.length === 0}
          defaultSortKey="dateCreated"
          customTheme={customTheme}
        />
      </div>

      <Pagination
        pageId={location?.search}
        results={filteredTickets.length}
        prevPage={prevPage}
        nextPage={nextPage}
        prevKey={currentPage > 1 ? String(currentPage - 1) : null}
        nextKey={currentPage < totalPages ? String(currentPage + 1) : null}
      />
    </Fragment>
  );
};

export default UnpaidTicketsTab;
